import { createBrowserRouter, RouterProvider, Outlet, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { SessionGuardProvider } from './contexts/SessionGuardContext';
import { Layout } from './components/layout/Layout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { RoleProtectedRoute } from './components/auth/RoleProtectedRoute';
import { FeatureProtectedRoute } from './components/auth/FeatureProtectedRoute';
import { ForcedChangePasswordGate } from './components/auth/ForcedChangePasswordGate';
import { IdleTimeoutHandler } from './components/auth/IdleTimeoutHandler';
import { MaintenanceGuard } from './routes/MaintenanceGuard';
import { VersionGuard } from './routes/VersionGuard';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { AccountPage } from './pages/AccountPage';
import { ProblemsPage } from './pages/ProblemsPage';
import { SessionSummaryPage } from './pages/SessionSummaryPage';
import { DashboardPage } from './pages/DashboardPage';
import { CountingPracticePage } from './pages/CountingPracticePage';
import { ChapterPage } from './pages/ChapterPage';
import { NotesSearchPage } from './pages/NotesSearchPage';
import { LearnPage } from './pages/LearnPage';
import { InventoryPage } from './pages/InventoryPage';
import { TopicPage } from './pages/TopicPage';
import { ModulePracticePage } from './pages/ModulePracticePage';
import { HowardHandsPage } from './pages/HowardHandsPage';
import { OvercallingDataEditingPage } from './pages/OvercallingDataEditingPage';
import { OvercallingAssignDifficultyPage } from './pages/OvercallingAssignDifficultyPage';
import { ThirdLevelBidReviewPage } from './pages/ThirdLevelBidReviewPage';
import { AskJeffReviewPage } from './pages/AskJeffReviewPage';
import { PredictedJeffBidsPage } from './pages/PredictedJeffBidsPage';
import { ConceptualCategoriesPage } from './pages/ConceptualCategoriesPage';
import { ConceptualProblemsPage } from './pages/ConceptualProblemsPage';
import { ForbiddenPage } from './pages/ForbiddenPage';
import { SessionExpiredPage } from './pages/SessionExpiredPage';
import { ErrorPage } from './pages/ErrorPage';
import { AccessibilityPage } from './pages/AccessibilityPage';
import { AdminPage } from './pages/AdminPage';
import { CreateUserPage } from './pages/CreateUserPage';
import { UserManagementPage } from './pages/UserManagementPage';
import { EditorActivityPage } from './pages/EditorActivityPage';
import { DataEditingLandingPage } from './pages/DataEditingLandingPage';
import { ForcedChangePasswordPage } from './pages/ForcedChangePasswordPage';

// Wrapper component to provide AuthProvider and SessionGuardProvider context inside the router
function RootLayout() {
  return (
    <AuthProvider>
      <SessionGuardProvider>
        <IdleTimeoutHandler />
        {/* Surface the nginx maintenance page on the next in-app navigation (FR-137) */}
        <MaintenanceGuard />
        {/* Reload a tab still running the bundle from before a deploy */}
        <VersionGuard />
        <Layout />
      </SessionGuardProvider>
    </AuthProvider>
  );
}

// Protected route wrapper that includes AuthProvider access. The
// ForcedChangePasswordGate sits inside ProtectedRoute so unauthenticated users
// still bounce to /login first (the gate only matters once you're signed in).
function ProtectedLayout() {
  return (
    <ProtectedRoute>
      <ForcedChangePasswordGate>
        <Outlet />
      </ForcedChangePasswordGate>
    </ProtectedRoute>
  );
}

// Create data router (required for useBlocker hook)
const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      // Public routes
      { index: true, element: <HomePage /> },
      { path: 'login', element: <LoginPage /> },
      { path: 'register', element: <RegisterPage /> },
      // Error/status pages (public - accessible without auth)
      { path: 'session-expired', element: <SessionExpiredPage /> },
      { path: 'error', element: <ErrorPage /> },
      // Maintenance is served by nginx as a static 503 page (public/maintenance.html),
      // not an SPA route — see PRODUCTION.md "Maintenance Mode".
      { path: 'forbidden', element: <ForbiddenPage /> },
      // Public Accessibility Statement page (010-accessibility-seo-basics, FR-014)
      { path: 'accessibility', element: <AccessibilityPage /> },
      // Feature 011 — Forced-change page is rendered WITHOUT the gate so the
      // locked user can break out of the lock here.
      {
        path: 'change-password',
        element: (
          <ProtectedRoute>
            <ForcedChangePasswordPage />
          </ProtectedRoute>
        ),
      },
      // Protected routes
      {
        element: <ProtectedLayout />,
        children: [
          { path: 'dashboard', element: <DashboardPage /> },
          // Feature 023 — the Study landing page re-homing the retired dashboard tab
          // (FR-005a). Declared before 'learn/:chapterSlug' so the static
          // 'learn/search' and index routes win over the param route.
          { path: 'learn', element: <LearnPage /> },
          // The Practice hub is retired: Overcalling, its last surface, is a dashboard door
          // card now. The path stays as a redirect so old bookmarks still land somewhere.
          { path: 'practice', element: <Navigate to="/dashboard" replace /> },
          // Feature 023 (US4/US6) — the Study · Practice · Master inventory.
          // The two-segment route is declared first so it is not shadowed by
          // the family-only route's param matching.
          { path: 'inventory/:family/:topicKey', element: <TopicPage /> },
          { path: 'inventory/:family', element: <InventoryPage /> },
          { path: 'module-practice/:family/:topicKey/:moduleSlug', element: <ModulePracticePage /> },
          // Feature 022 — Search Precision Notes by bidding sequence (authenticated)
          { path: 'learn/search', element: <NotesSearchPage /> },
          // Feature 021 — Learn book-chapter reader (authenticated; content is gated via /api/learn)
          { path: 'learn/:chapterSlug', element: <ChapterPage /> },
          { path: 'account', element: <AccountPage /> },
          { path: 'problems', element: <ProblemsPage /> },
          { path: 'counting-practice', element: <CountingPracticePage /> },
          // Feature 009 + 011: Hands for Howard role-gated to Admin/Editor and
          // additionally feature-scoped to howard_hands grant.
          {
            path: 'howard-hands',
            element: (
              <RoleProtectedRoute allowedRoles={['Admin', 'Editor', 'Super Editor']}>
                <FeatureProtectedRoute featureCode="howard_hands">
                  <HowardHandsPage />
                </FeatureProtectedRoute>
              </RoleProtectedRoute>
            ),
          },
          // Feature 009 + 011: Overcalling Data Editing role-gated + feature-scoped.
          {
            path: 'overcalling-data-editing',
            element: (
              <RoleProtectedRoute allowedRoles={['Admin', 'Editor', 'Super Editor']}>
                <FeatureProtectedRoute featureCode="overcalling_data_editing">
                  <OvercallingDataEditingPage />
                </FeatureProtectedRoute>
              </RoleProtectedRoute>
            ),
          },
          // Feature 012: Overcalling Assign Level of Difficulty role-gated + feature-scoped.
          {
            path: 'overcalling-assign-difficulty',
            element: (
              <RoleProtectedRoute allowedRoles={['Admin', 'Editor', 'Super Editor']}>
                <FeatureProtectedRoute featureCode="overcalling_assign_difficulty">
                  <OvercallingAssignDifficultyPage />
                </FeatureProtectedRoute>
              </RoleProtectedRoute>
            ),
          },
          // Feature 017: 3rd level bids — Review (read-only) role-gated + feature-scoped.
          {
            path: 'third-level-bid-review',
            element: (
              <RoleProtectedRoute allowedRoles={['Admin', 'Editor', 'Super Editor']}>
                <FeatureProtectedRoute featureCode="third_level_bid_review">
                  <ThirdLevelBidReviewPage />
                </FeatureProtectedRoute>
              </RoleProtectedRoute>
            ),
          },
          // Feature 025: Predicted Jeff's Bids by AI (read-only) role-gated + feature-scoped.
          {
            path: 'predicted-jeff-bids',
            element: (
              <RoleProtectedRoute allowedRoles={['Admin', 'Editor', 'Super Editor']}>
                <FeatureProtectedRoute featureCode="predicted_jeff_bids">
                  <PredictedJeffBidsPage />
                </FeatureProtectedRoute>
              </RoleProtectedRoute>
            ),
          },
          // Feature 029: Conceptual Problems (read-only) role-gated + feature-scoped. Three
          // addresses for one surface: the category list, a category (which redirects to its first
          // problem), and one problem by serial number — the serial rather than a position, so a
          // shared link cannot drift onto a different problem when the derived category is
          // recomputed on re-import.
          {
            path: 'conceptual-problems',
            element: (
              <RoleProtectedRoute allowedRoles={['Admin', 'Editor', 'Super Editor']}>
                <FeatureProtectedRoute featureCode="conceptual_problems">
                  <ConceptualCategoriesPage />
                </FeatureProtectedRoute>
              </RoleProtectedRoute>
            ),
          },
          {
            path: 'conceptual-problems/:categoryId',
            element: (
              <RoleProtectedRoute allowedRoles={['Admin', 'Editor', 'Super Editor']}>
                <FeatureProtectedRoute featureCode="conceptual_problems">
                  <ConceptualProblemsPage />
                </FeatureProtectedRoute>
              </RoleProtectedRoute>
            ),
          },
          {
            path: 'conceptual-problems/:categoryId/:serial',
            element: (
              <RoleProtectedRoute allowedRoles={['Admin', 'Editor', 'Super Editor']}>
                <FeatureProtectedRoute featureCode="conceptual_problems">
                  <ConceptualProblemsPage />
                </FeatureProtectedRoute>
              </RoleProtectedRoute>
            ),
          },
          // Feature 025: Ask Jeff — Review. Role-gated to Super Editor ALONE, with no
          // FeatureProtectedRoute wrapper: a feature-catalog grant is something an Admin hands
          // out, and this surface is deliberately closed to Admin as well.
          {
            path: 'ask-jeff-review',
            element: (
              <RoleProtectedRoute allowedRoles={['Super Editor']}>
                <AskJeffReviewPage />
              </RoleProtectedRoute>
            ),
          },
          // Feature 011 — Data Editing landing page replaces the old dropdown.
          { path: 'data-editing', element: <DataEditingLandingPage /> },
          // Feature 011 — Admin section
          {
            path: 'admin',
            element: (
              <RoleProtectedRoute allowedRoles={['Admin']}>
                <AdminPage />
              </RoleProtectedRoute>
            ),
          },
          {
            path: 'admin/users',
            element: (
              <RoleProtectedRoute allowedRoles={['Admin']}>
                <UserManagementPage />
              </RoleProtectedRoute>
            ),
          },
          {
            path: 'admin/users/new',
            element: (
              <RoleProtectedRoute allowedRoles={['Admin']}>
                <CreateUserPage />
              </RoleProtectedRoute>
            ),
          },
          // Feature 015 — Editor Activity (Admin only)
          {
            path: 'admin/editor-activity',
            element: (
              <RoleProtectedRoute allowedRoles={['Admin']}>
                <EditorActivityPage />
              </RoleProtectedRoute>
            ),
          },
          // Feature 023 (US5) — the scorecard, reached only by leaving a table. Serves module
          // practice, /problems and Counting alike; which one is in the location state's `kind`.
          // (The pre-023 `/summary` page retired with the 028 `_old` cleanup: Counting practice
          // was its last caller.)
          { path: 'session-summary', element: <SessionSummaryPage /> },
        ],
      },
    ],
  },
]);

export default function App() {
  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  );
}
